import { type fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import type MindMapLeaf from '../objects/MindMapLeaf';
import type MindMap from '../objects/MindMap';
import { MINDMAP_ANIMATION_DURATION } from '../objects/MindMap';
import { isMindMapLeaf, isMindMap } from '../objects/guards';
import { ShapeConnectControlAnimator } from '../utils/controlAnimation';

const SIZE = 18;
const SIZE_MINI = 8;
const SIZE_HOVER = 24;
const SIZE_MINI_HOVER = 20;
const HOVER_MIN_SIZE = 75;

class AddMindMapLeafControl extends ControlBase {
  actionName = 'Add child';
  cursorStyle = 'pointer';
  withConnection = false;
  sizeX = SIZE_HOVER;
  sizeY = SIZE_HOVER;
  mini = false;
  private readonly animator: ShapeConnectControlAnimator;

  constructor(options: { x: number; y: number; mini?: boolean }) {
    super(options);
    this.mini = !!options.mini;
    this.animator = new ShapeConnectControlAnimator(
      this.mini ? SIZE_MINI : SIZE,
      this.mini ? SIZE_MINI_HOVER : SIZE_HOVER
    );
    if (!this.mini) {
      this.animator.animationDuration /= 2;
    }
    this.offsetX = this.sizeX * options.x;
    this.offsetY = this.sizeY * options.y;
  }

  getMouseUpHandler(): fabric.ControlMouseEventHandler {
    return (_eventData, transformData) => {
      const object = transformData.target as MindMapLeaf | MindMap;
      if (object) {
        let mindmap: MindMap;
        let child: MindMapLeaf;

        if (isMindMap(object)) {
          // Control on central MindMap object
          mindmap = object;
          // Determine which side to add to based on control position
          let side: 'east' | 'west' | 'north' | 'south';
          if (this.offsetX > 0) side = 'east';
          else if (this.offsetX < 0) side = 'west';
          else if (this.offsetY < 0) side = 'north';
          else side = 'south';

          const branch = mindmap.getOrCreateBranch(side);
          child = mindmap.createChildInBranch(branch);
        } else {
          // Control on MindMapLeaf object
          mindmap = object.getMindMap()!;
          const branch = object.getBranch()!;
          const index = branch.leaves.indexOf(object);

          if (['east', 'west'].includes(branch.side) && this.offsetY !== 0) {
            // Siblings
            child = mindmap.createChildInBranch(
              branch,
              {},
              this.offsetY < 0 ? index : index + 1
            );
          } else if (
            ['north', 'south'].includes(branch.side) &&
            this.offsetX !== 0
          ) {
            // Siblings
            child = mindmap.createChildInBranch(
              branch,
              {},
              this.offsetX < 0 ? index : index + 1
            );
          } else {
            // Child
            child = mindmap.createChild(object);
          }
        }

        // Have to wait for animation
        setTimeout(() => {
          child.canvas.setActiveObject(child);
          child.startTextEditing(true);
        }, MINDMAP_ANIMATION_DURATION * 1.1);
      }
      return true;
    };
  }

  onMouseOver(eventData: MouseEvent, fabricObject: fabric.Object) {
    this.animator.animate('enter', {
      onRequestRender: () => {
        fabricObject?.canvas?.requestRenderAll();
      },
    });
    super.onMouseOver(eventData, fabricObject);
  }

  onMouseOut(eventData: MouseEvent, fabricObject: fabric.Object) {
    this.animator.animate('exit', {
      onRequestRender: () => {
        fabricObject?.canvas?.requestRenderAll();
      },
    });
    super.onMouseOut(eventData, fabricObject);
  }

  getVisibility(leaf: fabric.Object, controlKey: string): boolean {
    if (!super.getVisibility(leaf, controlKey)) return false;
    if (!isMindMapLeaf(leaf) && !isMindMap(leaf)) return false;

    const zoom = leaf.controller.canvas.getZoom();
    const width = leaf.width * leaf.scaleX * zoom;

    // Never visible if we're too small
    if (width < this.minDimension) {
      return false;
    }

    const isSelected = leaf.canvas?.getActiveObjects().includes(leaf);
    let isVisible = this.hovered || isSelected;

    // No hover if we're a bit too small
    if (width > HOVER_MIN_SIZE) {
      isVisible ||= leaf.hovered;
    }

    return isVisible;
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: object,
    parentObject: MindMapLeaf | MindMap
  ) {
    ctx.save();

    const size = this.animator.currentSize;

    const enableDebug = parentObject.controller.state.debug.enableMindMapDebug;

    const p = {
      x: left + (this.sizeX / 2 - SIZE_HOVER / 2) * this.x,
      y: top + (this.sizeY / 2 - SIZE_HOVER / 2) * this.y,
    };

    const isActive = this.animator.atMaxSize;

    // Background circle
    if (isActive) {
      ctx.fillStyle = '#0073CF';
    } else {
      ctx.fillStyle = '#5CA5E0';
    }
    ctx.strokeStyle = isActive ? '#0073CF' : 'white';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, size / 2, 0, 2 * Math.PI, false);
    ctx.fill();
    ctx.stroke();

    if (size > 12) {
      const offset = 6;
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#fff';
      ctx.lineCap = 'round';
      // Draw the vertical line of the plus
      ctx.beginPath();

      ctx.moveTo(p.x - size / 2 + offset, p.y);
      ctx.lineTo(p.x + size / 2 - offset, p.y);

      ctx.moveTo(p.x, p.y - size / 2 + offset);
      ctx.lineTo(p.x, p.y + size / 2 - offset);

      ctx.stroke();
    }

    if (enableDebug) {
      // Draw hitbox
      ctx.fillStyle = 'hsla(0, 50%, 50%, 0.2)';
      ctx.fillRect(
        left - this.sizeX / 2,
        top - this.sizeY / 2,
        this.sizeX,
        this.sizeY
      );
    }
    ctx.restore();
  }
}
export default AddMindMapLeafControl;
